import { useEffect } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  createTechDocsAddonExtension,
  TechDocsAddonLocations,
  useShadowRoot,
} from '@backstage/plugin-techdocs-react';
import { createPlugin } from '@backstage/core-plugin-api';

const STYLE_ID = 'backstage-theme-injection';

/** `#rgb` / `#rrggbb` → `rgba(r, g, b, alpha)`. Non-hex input is returned as-is. */
const withAlpha = (color: string, alpha: number) => {
  const hex = color.trim();
  if (!hex.startsWith('#')) return hex;
  const digits =
    hex.length === 4
      ? hex
          .slice(1)
          .split('')
          .map(c => c + c)
          .join('')
      : hex.slice(1, 7);
  if (digits.length !== 6) return hex;
  const int = parseInt(digits, 16);
  // eslint-disable-next-line no-bitwise
  return `rgba(${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}, ${alpha})`;
};

/**
 * Syntax highlighting palette. These are deliberately *not* derived from the
 * theme palette: theme colours are chosen for UI surfaces and produce
 * unreadable token colours (e.g. keywords rendered in `primary.dark` on a dark
 * code background). These are MkDocs Material's own token colours, which are
 * tuned for contrast against a light and a dark code surface respectively.
 */
const codeHighlight = {
  light: {
    number: '#d52a2a',
    special: '#db1457',
    function: '#a846b9',
    constant: '#6e59d9',
    keyword: '#3f6ec6',
    string: '#1c7d4d',
  },
  dark: {
    // Brightened from Material's slate values: those clear 4.5:1 against a deep
    // surface but only reach ~4.0:1 on the lighter dark themes (Slate Dimmed's
    // #2d333b is the tightest). These hold >= 5.2:1 on every dark theme.
    number: '#f08a80',
    special: '#f58cb4',
    function: '#d183e8',
    constant: '#a48ceb',
    keyword: '#6d9bea',
    string: '#2fbc74',
  },
};

const TechDocsThemeAddonComponent = () => {
  const theme = useTheme();
  const shadowRoot = useShadowRoot();

  useEffect(() => {
    if (!shadowRoot) return undefined;

    shadowRoot.querySelector(`#${STYLE_ID}`)?.remove();

    const p = theme.palette;
    const isDark = p.type === 'dark';

    const bg = p.background.default;
    const paper = p.background.paper;
    const fg = p.text.primary;
    const primary = p.primary.main;
    const priLight = p.primary.light;
    const priDark = p.primary.dark;
    const priContra = p.primary.contrastText;
    const secMain = p.secondary.main;
    const divider = p.divider;
    const hl = isDark ? codeHighlight.dark : codeHighlight.light;

    // Material derives borders, table headers, muted text and hover surfaces
    // from a *tonal ramp* of the foreground colour. Collapsing that ramp onto
    // `text.secondary` is what made table headers blend into the page, so keep
    // it as alpha steps over whatever the page background happens to be.
    const fgLight = withAlpha(fg, isDark ? 0.56 : 0.54);
    const fgLighter = withAlpha(fg, 0.32);
    const fgLightest = withAlpha(fg, isDark ? 0.12 : 0.07);

    // Comments, operators and punctuation are read as prose, so they need real
    // contrast — not the border tint. The ramp's own `--light` step drops to
    // 3.1:1 on Slate Dimmed, so these get their own, stronger alpha.
    const codeMuted = withAlpha(fg, isDark ? 0.8 : 0.7);

    // A code surface must read as distinct from the page. `background.paper`
    // equals `background.default` in some themes, so fall back to a tint.
    const codeBg =
      paper.toLowerCase() === bg.toLowerCase() ? withAlpha(fg, isDark ? 0.1 : 0.05) : paper;

    const css = `
      /* Theme colours only — everything below is a variable MkDocs Material
         already consumes. Resist adding rule overrides here: Material's own
         stylesheet is fully variable-driven and stays internally consistent.
         The scheme attribute is listed alongside :host because the built docs
         re-declare this palette on that element, and a custom property set on a
         descendant beats the one inherited from the shadow host. */
      :host,
      [data-md-color-scheme] {
        --md-text-font-family:             ${theme.typography.fontFamily};

        --md-default-bg-color:             ${bg};
        --md-default-bg-color--light:      ${withAlpha(bg, 0.7)};
        --md-default-bg-color--lighter:    ${withAlpha(bg, 0.3)};
        --md-default-bg-color--lightest:   ${withAlpha(bg, 0.12)};
        --md-default-fg-color:             ${fg};
        --md-default-fg-color--light:      ${fgLight};
        --md-default-fg-color--lighter:    ${fgLighter};
        --md-default-fg-color--lightest:   ${fgLightest};

        --md-primary-fg-color:             ${primary};
        --md-primary-fg-color--light:      ${priLight};
        --md-primary-fg-color--dark:       ${priDark};
        --md-primary-bg-color:             ${priContra};
        --md-primary-bg-color--light:      ${withAlpha(priContra, 0.7)};

        --md-accent-fg-color:              ${secMain};
        --md-accent-fg-color--transparent: ${withAlpha(secMain, 0.1)};
        --md-accent-bg-color:              ${priContra};

        --md-code-bg-color:                ${codeBg};
        --md-code-fg-color:                ${fg};
        --md-code-hl-color:                ${withAlpha(secMain, 0.2)};
        --md-code-hl-number-color:         ${hl.number};
        --md-code-hl-special-color:        ${hl.special};
        --md-code-hl-function-color:       ${hl.function};
        --md-code-hl-constant-color:       ${hl.constant};
        --md-code-hl-keyword-color:        ${hl.keyword};
        --md-code-hl-string-color:         ${hl.string};
        --md-code-hl-name-color:           ${fg};
        --md-code-hl-operator-color:       ${codeMuted};
        --md-code-hl-punctuation-color:    ${codeMuted};
        --md-code-hl-comment-color:        ${codeMuted};
        --md-code-hl-generic-color:        ${codeMuted};
        --md-code-hl-variable-color:       ${codeMuted};

        --md-typeset-color:                ${fg};
        --md-typeset-a-color:              ${primary};
        --md-typeset-del-color:            ${withAlpha(hl.number, 0.15)};
        --md-typeset-ins-color:            ${withAlpha(hl.string, 0.15)};
        --md-typeset-mark-color:           ${withAlpha(secMain, 0.2)};
        --md-typeset-table-color:          ${divider};
        --md-typeset-table-color--light:   ${withAlpha(fg, 0.035)};

        --md-admonition-bg-color:          ${codeBg};
        --md-admonition-fg-color:          ${fg};

        --md-footer-bg-color:              ${paper};
        --md-footer-bg-color--dark:        ${bg};
        --md-footer-fg-color:              ${fg};
        --md-footer-fg-color--light:       ${fgLight};
        --md-footer-fg-color--lighter:     ${fgLighter};

        --md-shadow-z1: ${isDark ? `0 0 0 1px ${divider}` : `0 1px 0 ${divider}`};
        --md-shadow-z2: ${isDark ? `0 0 0 1px ${divider}` : `0 2px 8px ${divider}`};
        --md-shadow-z3: ${isDark ? `0 0 0 1px ${divider}, 0 4px 16px rgba(0, 0, 0, 0.24)` : `0 4px 16px ${divider}`};
      }

      /* ── Syntax tokens ───────────────────────────────────────── */
      /* Material maps most Pygments classes to the --md-code-hl-* variables
         above, but only the ones its own scheme styles. Docs built against the
         light scheme ship concrete colours for the rest, which land near-black
         on a dark code surface (measured 1.11:1 on a Dockerfile FROM image
         reference). Mapping the full token set to the theme keeps every class
         accounted for regardless of how the site was built. */
      .md-typeset .highlight .c, .md-typeset .highlight .ch,
      .md-typeset .highlight .cm, .md-typeset .highlight .c1,
      .md-typeset .highlight .cs {
        color: var(--md-code-hl-comment-color);
      }
      .md-typeset .highlight .k, .md-typeset .highlight .kc,
      .md-typeset .highlight .kd, .md-typeset .highlight .kn,
      .md-typeset .highlight .kp, .md-typeset .highlight .kr,
      .md-typeset .highlight .kt, .md-typeset .highlight .kv {
        color: var(--md-code-hl-keyword-color);
      }
      .md-typeset .highlight .s, .md-typeset .highlight .sa,
      .md-typeset .highlight .sb, .md-typeset .highlight .sc,
      .md-typeset .highlight .s1, .md-typeset .highlight .s2,
      .md-typeset .highlight .sd, .md-typeset .highlight .se,
      .md-typeset .highlight .sh, .md-typeset .highlight .si,
      .md-typeset .highlight .sx, .md-typeset .highlight .ss,
      .md-typeset .highlight .dl, .md-typeset .highlight .sr {
        color: var(--md-code-hl-string-color);
      }
      .md-typeset .highlight .m, .md-typeset .highlight .mb,
      .md-typeset .highlight .mf, .md-typeset .highlight .mh,
      .md-typeset .highlight .mi, .md-typeset .highlight .mo,
      .md-typeset .highlight .il {
        color: var(--md-code-hl-number-color);
      }
      .md-typeset .highlight .nf, .md-typeset .highlight .fm {
        color: var(--md-code-hl-function-color);
      }
      .md-typeset .highlight .no, .md-typeset .highlight .nb,
      .md-typeset .highlight .bp {
        color: var(--md-code-hl-constant-color);
      }
      .md-typeset .highlight .o, .md-typeset .highlight .ow {
        color: var(--md-code-hl-operator-color);
      }
      .md-typeset .highlight .p {
        color: var(--md-code-hl-punctuation-color);
      }
      .md-typeset .highlight .nv, .md-typeset .highlight .vc,
      .md-typeset .highlight .vg, .md-typeset .highlight .vi,
      .md-typeset .highlight .vm {
        color: var(--md-code-hl-variable-color);
      }
      /* Everything left over — plain text, names, attributes, tags, labels —
         reads as ordinary code and must never be dimmer than the body text. */
      .md-typeset .highlight .n, .md-typeset .highlight .na,
      .md-typeset .highlight .nc, .md-typeset .highlight .nd,
      .md-typeset .highlight .ni, .md-typeset .highlight .ne,
      .md-typeset .highlight .nl, .md-typeset .highlight .nn,
      .md-typeset .highlight .nx, .md-typeset .highlight .nt,
      .md-typeset .highlight .py, .md-typeset .highlight .w,
      .md-typeset .highlight .err {
        color: var(--md-code-hl-name-color);
      }

      /* ── Layout, not colour ──────────────────────────────────── */

      /* TechDocs renders the page title and ToC itself; hide the duplicates. */
      .md-typeset .headerlink { display: none; }

      .md-nav__toggle { position: absolute; left: -9999px; opacity: 0; }

      /* Keep the ToC in the right sidebar only. */
      .md-sidebar--primary .md-nav--secondary,
      .md-sidebar--primary input[for="__toc"],
      .md-sidebar--primary label[for="__toc"] {
        display: none;
      }
      .md-sidebar--secondary .md-nav--secondary { display: block; }

      /* Secondary sidebar sits on the page background, not its own surface. */
      .md-sidebar--secondary,
      .md-sidebar--secondary .md-sidebar__scrollwrap,
      .md-sidebar--secondary .md-sidebar__inner {
        background-color: transparent;
      }

      /* ── Footer: pin prev/next as floating buttons ───────────── */
      .md-footer {
        background-color: transparent;
        position: fixed;
        bottom: 0;
        pointer-events: none;
        margin: 0;
        padding: 0;
        border: none;
      }

      .md-footer__inner {
        position: relative;
        max-width: 100%;
        margin: 0;
        min-height: 0;
        justify-content: space-between;
        overflow: auto;
        padding: .2rem;
      }

      .md-footer__inner:not([hidden]) { display: flex; }

      /* Hide the grid itself but let the repositioned links escape it. */
      .md-footer__inner .md-grid {
        opacity: 0;
        height: 0;
        overflow: visible;
        position: relative;
      }

      .md-footer__inner .md-grid a[href] {
        visibility: visible;
        opacity: 1;
        position: fixed;
        top: 50%;
        transform: translateY(-50%);
        width: auto;
        min-width: 220px;
        max-width: 500px;
        display: inline-block;
        color: var(--md-default-fg-color);
        text-decoration: none;
        font-weight: 500;
        padding: 12px 16px;
        border-radius: 6px;
        border: 1px solid var(--md-typeset-table-color);
        background-color: var(--md-default-bg-color);
        z-index: 1000;
        transition: all 0.2s;
        box-shadow: var(--md-shadow-z2);
        pointer-events: auto;
        white-space: nowrap;
      }

      .md-footer__inner .md-grid a[href] * {
        white-space: nowrap;
        overflow: visible;
        text-overflow: unset;
        flex-shrink: 0;
        width: auto;
        max-width: none;
        min-width: 0;
      }

      .md-footer__inner .md-grid a[href]:first-child { left: 20px; }

      .md-footer__inner .md-grid a[href]:nth-child(2) {
        right: 20px;
        justify-content: flex-end;
        text-align: right;
      }

      .md-footer__inner .md-grid a[href]:hover {
        color: var(--md-primary-fg-color);
        border-color: var(--md-primary-fg-color);
      }

      .md-footer-meta { background-color: transparent; }

      .md-footer-meta__inner {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        padding: .2rem;
      }

      .md-footer__link { opacity: 1; }
      .md-footer__title, .md-footer__direction { opacity: 1; }
    `;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = css;
    shadowRoot.appendChild(style);

    return () => {
      shadowRoot.querySelector(`#${STYLE_ID}`)?.remove();
    };
  }, [shadowRoot, theme]);

  return null;
};

const techDocsThemePlugin = createPlugin({ id: 'techDocsTheme' });

export const TechDocsThemeAddon = techDocsThemePlugin.provide(
  createTechDocsAddonExtension({
    name: 'TechDocsTheme',
    location: TechDocsAddonLocations.Content,
    component: TechDocsThemeAddonComponent,
  }),
);
